import React, { useMemo, useRef, useState } from "react";
import {
  Box, HStack, Text, Button, ButtonGroup, Menu, MenuButton, MenuList, MenuItem, Tooltip as ChakraTooltip,
} from "@chakra-ui/react";
import { FiChevronDown, FiCheck } from "react-icons/fi";
import {
  ResponsiveContainer, ComposedChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid,
} from "recharts";
import {
  buildChart, listRuns, yAxis, clusterEvents, timeTicks, formatTick, formatMoment, formatMbShort,
} from "./memoryChartData";

export const DARK = {
  panel: "#0e0e10", border: "#2a2a2e", grid: "#2f2f33", axis: "#a1a1aa", text: "#f4f4f5",
  muted: "#a1a1aa", menu: "#17171a", hover: "#232327", selected: "#2e1065", accent: "#7c3aed",
};

/** The numbered box on top of an event line, like Render's. Pointing at it shows what happened. */
function EventBadge({ viewBox, count, label, onHover, onClick }) {
  if (!viewBox) return null;
  const { x, y } = viewBox;
  const width = count > 9 ? 22 : 18;
  return (
    <g style={{ cursor: "pointer" }} onClick={onClick} onMouseEnter={() => onHover({ x, y })}
       onMouseLeave={() => onHover(null)} role="button" aria-label={label} data-testid="event-badge">
      <rect x={x - width / 2} y={y - 22} width={width} height={18} rx={2} fill={DARK.panel} stroke="#d4d4d8" strokeWidth={1} />
      <text x={x} y={y - 9} textAnchor="middle" fill={DARK.text} fontSize={11}>{count}</text>
    </g>
  );
}

function ChartTooltip({ active, payload, label, lines, nameOf, mode }) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload || {};
  const byKey = new Map(lines.map((line) => [line.key, line]));
  return (
    <Box bg={DARK.menu} borderWidth={1} borderColor={DARK.border} borderRadius="md" p={2} fontSize="xs" color={DARK.text}
         boxShadow="lg" maxW="380px">
      <Text fontWeight="semibold" mb={1}>{formatMoment(label ?? row.t)}</Text>
      {payload.filter((entry) => entry.value != null && byKey.has(entry.dataKey)).map((entry) => {
        const line = byKey.get(entry.dataKey);
        const mb = row[`${entry.dataKey}__mb`];
        const pct = line.limit_mb ? `${Math.round((mb / line.limit_mb) * 100)}%` : null;
        const value = mode === "percent" ? `${pct} · ${formatMbShort(mb)}` : `${formatMbShort(mb)}${pct ? ` · ${pct}` : ""}`;
        return (
          <HStack key={entry.dataKey} spacing={2} align="baseline">
            <Box w="8px" h="8px" borderRadius="full" bg={line.color} flexShrink={0} />
            <Text color={DARK.muted} noOfLines={1}>{nameOf(line)}</Text>
            <Text fontWeight="semibold" ml="auto" whiteSpace="nowrap">{value}</Text>
          </HStack>
        );
      })}
    </Box>
  );
}

function limitText(limits) {
  if (!limits.length) return null;
  return limits.map(formatMbShort).join(" · ");
}

/**
 * The memory of the server answering (this computer on localhost, the Render service on
 * Render), drawn the way Render's memory graph is: one line per run of the server (each restart
 * or deploy starts a new colour, and Instances lists the runs), memory as a share of its limit
 * (or in MB under Total, on the same quarters), and a dashed marker with a count wherever
 * something happened. Pointing at a marker says what happened; clicking it opens that event in
 * the list below the chart. When the backend can't tell which server it is, every server is drawn.
 */
export default function MemoryChart({ memory, windowStart, windowEnd, events, onSelectEvent }) {
  const [mode, setMode] = useState("percent");
  const [chosenRun, setRun] = useState("all");
  const [hover, setHover] = useState(null);
  const plotRef = useRef(null);
  const groups = useMemo(() => new Map((memory?.groups || []).map((g) => [g.key, g])), [memory]);
  const runs = useMemo(() => listRuns(memory, Date.parse(windowEnd)), [memory, windowEnd]);
  // A run can drop out of the window (the range changed, or the live window moved past it).
  const selected = runs.find((r) => r.run === chosenRun) || null;
  const run = selected ? chosenRun : "all";
  // A computer has no memory limit, so there's nothing to take a percentage of.
  const anyLimit = (memory?.lines || []).some((line) => line.limit_mb);
  const view = anyLimit ? mode : "total";
  const chart = useMemo(() => buildChart(memory, { mode: view, run }), [memory, view, run]);
  const domain = useMemo(() => {
    const from = Date.parse(windowStart);
    const to = Date.parse(windowEnd);
    if (!Number.isNaN(from) && !Number.isNaN(to) && to > from) return [from, to];
    const ts = chart.rows.map((r) => r.t);
    return ts.length ? [ts[0], ts[ts.length - 1]] : [0, 1];
  }, [windowStart, windowEnd, chart.rows]);
  const ticks = useMemo(() => timeTicks(domain), [domain]);
  // Markers only where lines are drawn: on a server that's drawn (a computer is hidden under
  // Percentage when several servers share the chart), and inside the chosen run.
  const drawn = useMemo(() => new Set(chart.lines.map((line) => line.group)), [chart.lines]);
  const markers = useMemo(() => {
    const bucketMs = (memory?.bucket_s || 60) * 1000;
    const shown = (events || []).filter((e) => {
      if (e.group && !drawn.has(e.group)) return false;
      if (!selected) return true;
      const ms = Date.parse(e.t);
      return ms >= selected.first - bucketMs && ms <= selected.last + 2 * bucketMs;
    });
    return clusterEvents(shown, domain);
  }, [events, drawn, selected, domain, memory]);
  const axis = yAxis(view, chart.peak, chart.limits);
  const runLabels = new Map(runs.map((r) => [r.run, r.label]));
  const serverName = (key) => groups.get(key)?.label || "A server";
  // In the tooltip: which run, when there's more than one on screen; else the server.
  const drawnRuns = new Set(chart.lines.map((line) => String(line.run ?? line.key)));
  const nameOf = (line) => (drawnRuns.size > 1 ? runLabels.get(String(line.run ?? line.key)) : serverName(line.group));
  const describe = (e) => `${formatMoment(e.ms)}: ${serverName(e.group)} ${e.what || e.kind}`;
  const shownLimits = selected ? (selected.limit_mb ? [selected.limit_mb] : [])
    : (chart.limits.length ? chart.limits : (memory?.limit_mb ? [memory.limit_mb] : []));
  const hasAny = (memory?.lines || []).length > 0;
  let server = null;
  if (groups.size === 1) server = [...groups.values()][0].label;
  else if (groups.size > 1) server = "Every server writing to this database";
  const popupLeft = hover && plotRef.current && hover.x > plotRef.current.clientWidth * 0.6;

  return (
    <Box data-testid="memory-chart">
      <HStack justify="space-between" flexWrap="wrap" spacing={3} mb={3}>
        <HStack spacing={2}>
          <Text fontSize="sm" color={DARK.muted}>Instances</Text>
          <Menu placement="bottom-start" isLazy>
            <MenuButton as={Button} size="sm" variant="ghost" color={DARK.text} rightIcon={<FiChevronDown />}
                        _hover={{ bg: DARK.hover }} _active={{ bg: DARK.hover }} data-testid="instances-filter">
              {selected ? selected.label : "All"}
            </MenuButton>
            <MenuList bg={DARK.menu} borderColor={DARK.border} color={DARK.text} zIndex={20} maxH="320px" overflowY="auto">
              <MenuItem bg={DARK.menu} _hover={{ bg: DARK.hover }} onClick={() => setRun("all")}
                        icon={run === "all" ? <FiCheck /> : <Box w="12px" />}>
                All
              </MenuItem>
              {runs.map((r) => (
                <MenuItem key={r.run} bg={DARK.menu} _hover={{ bg: DARK.hover }} onClick={() => setRun(r.run)}
                          icon={r.run === run ? <FiCheck /> : <Box w="12px" />}>
                  <HStack spacing={2}>
                    <Box w="8px" h="8px" borderRadius="full" bg={r.color} />
                    <Text>{r.label}</Text>
                    {r.current && <Text fontSize="xs" color="#86efac">running now</Text>}
                  </HStack>
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </HStack>
        <ButtonGroup size="sm" isAttached variant="outline">
          {[["percent", "Percentage"], ["total", "Total"]].map(([value, label]) => (
            <Button key={value} onClick={() => setMode(value)} aria-pressed={view === value}
                    isDisabled={value === "percent" && !anyLimit}
                    color={DARK.text} borderColor={view === value ? DARK.accent : DARK.border}
                    bg={view === value ? DARK.selected : "transparent"} _hover={{ bg: view === value ? DARK.selected : DARK.hover }}>
              {label}
            </Button>
          ))}
        </ButtonGroup>
      </HStack>

      <HStack spacing={2} align="baseline" flexWrap="wrap">
        <Text fontSize="lg" fontWeight="bold" color={DARK.text}>Memory</Text>
        {server && <Text fontSize="sm" color={DARK.text} data-testid="memory-server">{server}</Text>}
      </HStack>
      <Text fontSize="xs" color={DARK.muted} mb={1}>
        {view === "percent" ? "How much memory it's using, as a share of its limit" : "How much memory it's using"}
      </Text>
      <HStack spacing={2} mb={1} fontSize="sm">
        <ChakraTooltip label="The most memory a server may use before Render stops it" hasArrow>
          <Text as="span" textDecoration="underline" color={DARK.muted} cursor="help">Limit</Text>
        </ChakraTooltip>
        <Text as="span" color={DARK.text} data-testid="memory-limit">{limitText(shownLimits) || "none"}</Text>
      </HStack>
      {memory?.note && <Text fontSize="xs" color="#fbbf24" mb={1}>{memory.note}</Text>}
      {hasAny && !anyLimit && (
        <Text fontSize="xs" color={DARK.muted} mb={1}>
          This computer has no memory limit, so there's no percentage to show: memory is shown in MB.
        </Text>
      )}
      {view === "percent" && chart.hidden > 0 && (
        <Text fontSize="xs" color={DARK.muted} mb={1}>
          Computers have no memory limit, so they're only drawn under Total.
        </Text>
      )}

      {!hasAny ? (
        <Box py={10} textAlign="center" color={DARK.muted} fontSize="sm">
          No memory samples in this window: the backend memory monitor may not be running.
        </Box>
      ) : (
        <Box position="relative" ref={plotRef}>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={chart.rows} margin={{ top: 28, right: 8, bottom: 0, left: 8 }}>
              <CartesianGrid vertical={false} stroke={DARK.grid} />
              <XAxis dataKey="t" type="number" scale="time" domain={domain} ticks={ticks}
                     tickFormatter={(v) => formatTick(v, ticks[ticks.indexOf(v) - 1])}
                     tick={{ fill: DARK.axis, fontSize: 11 }} axisLine={{ stroke: DARK.grid }} tickLine={false} />
              <YAxis orientation="right" domain={axis.domain} ticks={axis.ticks} width={56} interval={0}
                     tickFormatter={(v) => (view === "percent" ? `${v}%` : formatMbShort(v))}
                     tick={{ fill: DARK.axis, fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip lines={chart.lines} nameOf={nameOf} mode={view} />}
                       cursor={{ stroke: DARK.muted, strokeDasharray: "2 2" }} />
              {markers.map((m) => (
                <ReferenceLine key={m.key} x={m.ms} stroke="#71717a" strokeDasharray="3 3" ifOverflow="hidden"
                               label={<EventBadge count={m.count} label={m.events.map(describe).join("; ")}
                                                  onHover={(pos) => setHover(pos ? { ...pos, marker: m } : null)}
                                                  onClick={() => {
                                                    const first = m.events.find((e) => e.select) || m.events[0];
                                                    if (onSelectEvent) onSelectEvent(first.select || first.t);
                                                  }} />} />
              ))}
              {chart.lines.map((line) => (
                <Line key={line.key} type="linear" dataKey={line.key} stroke={line.color} strokeWidth={1.5}
                      dot={false} activeDot={{ r: 3 }} isAnimationActive={false} connectNulls />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
          {hover && (
            <Box position="absolute" left={`${hover.x}px`} top={`${hover.y + 4}px`} zIndex={10} pointerEvents="none"
                 transform={popupLeft ? "translateX(-100%)" : "translateX(-50%)"} data-testid="event-popup"
                 bg={DARK.menu} borderWidth={1} borderColor={DARK.border} borderRadius="md" p={2} boxShadow="lg"
                 fontSize="xs" color={DARK.text} maxW="360px" minW="200px">
              {hover.marker.events.map((e, i) => (
                <Text key={`${e.t}-${i}`} mb={i < hover.marker.events.length - 1 ? 1 : 0}>
                  <Text as="span" color={DARK.muted}>{formatMoment(e.ms)}</Text>{" "}
                  {serverName(e.group)} {e.what || e.kind}
                </Text>
              ))}
              {hover.marker.events.some((e) => e.select) && (
                <Text color={DARK.muted} mt={1}>Click for who and what was involved.</Text>
              )}
            </Box>
          )}
        </Box>
      )}

      {run === "all" && runs.length > 1 && (
        <Text fontSize="xs" color={DARK.muted} mt={2}>
          Each colour is one run of the server: a restart or a deploy starts a new one. Pick one under Instances to see it alone.
        </Text>
      )}
    </Box>
  );
}
